import React, { useState, useRef, useEffect } from 'react';
import { Formik, Form, ErrorMessage, Field } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { SuccessModal, ICONS, Select } from '@ohif/ui';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import './importStudyModal.css';
import { ImportStudyModalProps } from '@custom/Utils/constants/interfaces';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { TextField } from '@mui/material';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _commonUrls, _dcm4cheeUploadUrls } from '@custom/Utils/constants/apiUrls';
import dcmjs from 'dcmjs';
import JSZip from 'jszip';
import { useTheme } from "../../../ui/src/contextProviders"
import { useUploadTracker } from './useUploadTracker';
import { getLocalPACSToken } from '@custom/Utils/authUtils';
import { servicesManager } from '../../../app/src/App';
import { customStyle } from '@custom/Utils/utils';
import FileInput from './importInput';

const StudyUploadModal: React.FC<ImportStudyModalProps & { patientData?: any }> = ({ isOpen, onClose, patientData }) => {

  const dispatch = useDispatch<AppDispatch>();
  const [successMessage, setSuccessMessage] = useState('');
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('')
  const { uiNotificationService } = servicesManager.services;
  ////////////////
  const [fileName, setFileName] = useState('No file Chosen');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef(null);
  const [ModalityOptions, setModalityOptions] = useState([]);
  const [selectedModality, setSelectedModality] = useState(null);
  const [selectedOption, setSelectedOption] = useState('DICOM');
  const [isUnzipping, setIsUnzipping] = useState(false);
  const [isImportDisabled, setIsImportDisabled] = useState(false);
  const { uploads,
    totalProgress,
    hasGlobalError,
    activeRequests,
    initializeUploads,
    updateProgress,
    resetUploads,
    isUploading,
    abortAllRequests,
    addActiveRequest,
    removeActiveRequest } = useUploadTracker();
  const [currentUploads, setCurrentUploads] = useState<XMLHttpRequest[]>([]);
  const { isDarkMode } = useTheme();

  const fetchCommonData = async () => {
    try {
      const commonData: any = await apiRequest({ method: 'POST', endUrl: _commonUrls.list, body: {}, headerType: 'json' })
      setModalityOptions(commonData?.result?.modality)
      setSelectedModality(commonData?.result?.modality[0])
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  const initialValues = {
    patientId: patientData?.uhid || '',
    patientName: patientData?.name || '',
    age: patientData?.age || '',
    gender: patientData?.gender || '',
    studyDate: dayjs(),
    modality: Array.isArray(patientData?.modality) ? patientData?.modality : [patientData?.modality] || [],
    studyDesc: '',
    accessionNum: patientData?.patientId || '',
    refPhysician: '',
    seriesDesc: '',
    fileSelection: null,
    studyRegion: patientData?.studyRegion || '',
  };

  const getValidationSchema = (selectedOption: string) => {
    if (selectedOption === 'DICOM') {
      return Yup.object().shape({
        fileSelection: Yup.mixed().required('File(s) or Zip or Folder is required.'),
      });
    } else if (selectedOption === 'nonDICOM') {
      return Yup.object().shape({
        patientId: Yup.string().required('Patient ID is required.'),
        patientName: Yup.string().required('Patient Name is required.'),
        accessionNum: Yup.string().required('Accession Number is required.'),
        age: Yup.string()
          .required('Age is required')
          .matches(
            /^(\d+\s*(Years?|Yrs?|Yr|Months?|Month|Days?|Day)\s*)+$/i,
            'Age must be numbers followed by Year(s) ,Month(s), or Day(s) (e.g., "2 Days", "1 Year 3 Months", "5 Yrs 10 Days")'
          ),
        gender: Yup.string().required('Gender is required.'),
        studyDate: Yup.date().required('Study Date is required.'),
        modality: Yup.array().min(1, 'At least one modality is required.'),
        fileSelection: Yup.mixed().required('File(s) or Zip or Folder is required.'), // File is also required for Non-DICOM
        studyRegion: Yup.string().optional(),
      });
    }
    return Yup.object(); // Default empty schema
  };

  const handleImport = async (values: any) => {
    resetUploads();
    if (!localStorage.getItem('local_pacs_access_token') && !sessionStorage.getItem('local_pacs_access_token')) {
      onClose();
      uiNotificationService.show({
        title: 'Import Study',
        message: 'No PACS is ACTIVE. Please ACTIVATE a PACS and try again.',
        type: 'error',
      });
      return;
    }
    if (files.length === 0) return;

    initializeUploads(files);
    setIsError(false);
    setErrorMessage('');

    try {
      const studyInstanceUid = generateDicomUid();
      const seriesInstanceUid = generateDicomUid();

      const uploadPromises = files.map((file, index) =>
        selectedOption === 'DICOM'
          ? uploadDICOMFile(file, values)
          : uploadNonDICOMFile(file, values, studyInstanceUid, seriesInstanceUid)
      );

      const results = await Promise.allSettled(uploadPromises);

      // Check if upload was cancelled
      const wasCancelled = results.some(result =>
        result.status === 'rejected' &&
        result.reason?.message?.includes('cancelled')
      );

      if (wasCancelled) {
        setErrorMessage('Upload cancelled');
        setIsError(true);
        return;
      }

      // Check if any upload failed with conflict
      const hasConflict = results.some(result =>
        result.status === 'rejected' &&
        result.reason?.message?.includes('conflict')
      );

      if (hasConflict) {
        // Update all uploads to show conflict
        files.forEach(file => {
          updateProgress(file.name, 0, 'Upload conflict detected');
        });
        throw new Error('Upload conflict detected');
      }

      // Check for other errors
      const otherErrors = results.filter(result => result.status === 'rejected');
      if (otherErrors.length > 0) {
        throw new Error('One or more files failed to upload');
      }

      setSuccessMessage('Files uploaded successfully.');
      setIsSuccessOpen(true);
      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (error) {
      setIsError(true);
      setErrorMessage(error.message);
    }
  };

  const createUploadRequest = (
    file: File,
    payload: Blob,
    boundary: string,
    isNonDicom: boolean
  ) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', _dcm4cheeUploadUrls.upload);

      // Set headers
      xhr.setRequestHeader('Authorization', `Bearer ${getLocalPACSToken()}`);
      if (isNonDicom) {
        xhr.setRequestHeader('Content-Type', `multipart/related; type="application/dicom+json"; boundary=${boundary}`);
      } else {
        xhr.setRequestHeader('Content-Type', `multipart/related; type="application/dicom"; boundary=${boundary}`);
      }
      xhr.setRequestHeader('Accept', 'application/dicom+json');

      // Progress handler
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          updateProgress(file.name, progress);
        }
      });

      // Completion handler
      xhr.onload = () => {
        removeActiveRequest(xhr);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.error && response.error.includes('conflict')) {
              updateProgress(file.name, 0, 'Upload conflict detected');
              reject(new Error('Upload conflict detected'));
              return;
            }
            updateProgress(file.name, 100);
            resolve(xhr.response);
          } catch (e) {
            updateProgress(file.name, 100);
            resolve(xhr.response);
          }
        } else if (xhr.status === 409) {
          updateProgress(file.name, 0, 'Upload conflict detected');
          reject(new Error('Upload conflict detected'));
        } else {
          updateProgress(file.name, 0, `Upload failed: ${xhr.statusText}`);
          reject(new Error(`Upload failed for ${file.name}`));
        }
      };

      // Error handler
      xhr.onerror = () => {
        removeActiveRequest(xhr);
        updateProgress(file.name, 0, 'Network error');
        reject(new Error(`Network error for ${file.name}`));
      };

      // Abort handler
      xhr.onabort = () => {
        removeActiveRequest(xhr);
        updateProgress(file.name, 0, 'Upload cancelled');
        reject(new Error(`Upload cancelled for ${file.name}`));
      };

      // Track and send request
      addActiveRequest(xhr);
      xhr.send(payload);
    });
  };
useEffect(() => {
  return () => {
    // Clean up any pending timeouts when component unmounts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };
}, []);

const handleCancelUploads = () => {
  abortAllRequests();
  resetUploads();
  setIsImportDisabled(false);
  setErrorMessage('Upload cancelled');
  setIsError(true);

  // Clear any existing timeout before setting a new one
  if (timeoutRef.current) {
    clearTimeout(timeoutRef.current);
  }

  // Set a new timeout to close the modal after 2 seconds
  timeoutRef.current = setTimeout(() => {
    onClose();
  }, 1000);
};

  const parseAgeForDicom = (ageInput: string): string => {
    if (!ageInput || typeof ageInput !== 'string') return '';

    // Regex for all pairs
    const pairRegex = /(\d+)\s*(Years?|Yrs?|Yr|Months?|Month|Days?|Day)/gi;

    let match;
    let totalDays = 0;

    while ((match = pairRegex.exec(ageInput)) !== null) {
      const number = parseInt(match[1], 10);
      const unit = match[2].toLowerCase();

      switch (unit) {
        case 'year':
        case 'years':
        case 'yr':
        case 'yrs':
          totalDays += number * 365;
          break;
        case 'month':
        case 'months':
          totalDays += number * 30;
          break;
        case 'day':
        case 'days':
          totalDays += number;
          break;
        default:
          return '';
      }
    }

    if (totalDays === 0) return '';

    // Decide which unit to output — usually the biggest possible unit
    if (totalDays >= 365) {
      const years = Math.floor(totalDays / 365);
      return years.toString().padStart(3, '0') + 'Y';
    } else if (totalDays >= 30) {
      const months = Math.floor(totalDays / 30);
      return months.toString().padStart(3, '0') + 'M';
    } else {
      return totalDays.toString().padStart(3, '0') + 'D';
    }
  };

  const uploadNonDICOMFile = async (file: File, values: any, studyInstanceUid: string, seriesInstanceUid: string) => {
    const boundary = `----Boundary${Math.random().toString().substr(2, 10)}`;
    const sopInstanceUid = generateDicomUid();
    const fileBuffer = await file.arrayBuffer();
    const fileName = file.name;
    const institutionName = sessionStorage.getItem('hospitalCode') || localStorage.getItem('hospitalCode');
    // Create the DICOM JSON metadata
    const dicomJson = JSON.stringify([
      {
        "00100010": {
          "vr": "PN",
          "Value": [
            {
              "Alphabetic": values.patientName || "" // Patient Name
            }
          ]
        },
        "00100020": {
          "vr": "LO",
          "Value": [patientData?.uhid || ""] // Patient ID
        },
        "00100021": {
          "vr": "LO",
          "Value": [institutionName || ""] // Optional, default value
        },
        "00100030": {
          "vr": "DA",
          "Value": [patientData?.dob?.replace(/-/g, '') || ""] // dob (Date of Birth) - Optional, empty
        },
        "00100040": {
          "vr": "CS",
          "Value": [values.gender || "M"] // Gender
        },
        "00101010": {
          "vr": "AS",
          "Value": [parseAgeForDicom(values.age)] // Patient Age
        },
        "00080090": {
          "vr": "PN",
          "Value": [values.refPhysician || ""] // Referring Physician
        },
        "00200010": {
          "vr": "SH",
          "Value": [values.accessionNum || ""] // Accession Number
        },
        "00080020": {
          "vr": "DA",
          "Value": [values.studyDate ? values.studyDate.format('YYYYMMDD') : ""] // Study Date (required, empty if unknown)
        },
        "00120062": {
          "vr": "CS",
          "Value": ["YES"] // Optional, default value
        },
        "00120063": {
          "vr": "LO",
          "Value": [
            "dcanon",
            "no identifiers",
            "keep descriptions",
            "keep manufacturer",
            "keep retired",
            "no private",
            "no uids",
            "keep pet demographics",
            "keep dates",
            "dcdtchg change dates"
          ]
        },
        "00081030": {
          "vr": "LO",
          "Value": [values.studyDesc || "Imported Image"] // Study Description
        },
        "0008103E": {
          "vr": "LO",
          "Value": [values.seriesDesc || "Series Description"] // Series Description
        },
        "00080018": {
          "vr": "UI",
          "Value": [sopInstanceUid] // SOP Instance UID (generated dynamically)
        },
        "00080030": {
          "vr": "TM",
          "Value": [""] // Optional, empty
        },
        "00080050": {
          "vr": "SH",
          "Value": [patientData?.patientId || ""] // Optional, empty
        },
        "00080016": {
          "vr": "UI",
          "Value": ["1.2.840.10008.5.1.4.1.1.7"] // Default SOP Class UID
        },
        "7FE00010": {
          "vr": "OB",
          "BulkDataURI": `file/${fileName}` // File name
        },
        "00080008": {
          "vr": "CS",
          "Value": ["ORIGINAL", "PRIMARY"] // Default values
        },
        "00080064": {
          "vr": "CS",
          "Value": ["WSD"] // Default value
        },
        "00200020": {
          "vr": "CS",
          // "Value": [""] // Optional, empty
        },
        "00200013": {
          "vr": "IS",
          "Value": [1] // Default value
        },
        "00200011": {
          "vr": "IS",
          "Value": [0] // Default value
        },
        "0020000D": {
          "vr": "UI",
          "Value": [studyInstanceUid] // Study Instance UID (generated dynamically)
        },
        "0020000E": {
          "vr": "UI",
          "Value": [seriesInstanceUid] // Series Instance UID (generated dynamically)
        },
        "00080060": {
          "vr": "CS",
          "Value": [values.modality.length > 0 ? values.modality[0] : "OT"] // Modality (default to "OT" if not provided)
        },
        "00080080": {
          "vr": "LO",
          "Value": [institutionName || ""] // Institution Name
        },
        "00180015": {
          "vr": "CS",
          "Value": [values.studyRegion ? values.studyRegion.toUpperCase() : ""]
        },
      }
    ]);
    const crlf = '\r\n';
    const dashes = '--';
    const multipartPayload = Buffer.concat([
      // Part 1: DICOM JSON metadata
      Buffer.from(`${dashes}${boundary}${crlf}`),
      Buffer.from(`Content-Type: application/dicom+json${crlf}${crlf}`),
      Buffer.from(`${dicomJson}${crlf}`),

      // Part 2: File data
      Buffer.from(`${dashes}${boundary}${crlf}`),
      Buffer.from(`Content-Type: ${getContentType(fileName)}${crlf}`),
      Buffer.from(`Content-Location: file/${fileName}${crlf}${crlf}`),
      Buffer.from(fileBuffer), // Append the file buffer as binary data
      Buffer.from(`${crlf}`),

      // End of multipart payload
      Buffer.from(`${dashes}${boundary}${dashes}${crlf}`),
    ]);

    // Create a Blob from the Buffer
    const payload = new Blob([multipartPayload], { type: `multipart/related; boundary=${boundary}` });

    return createUploadRequest(file, payload, boundary, true);
  };

  const uploadDICOMFile = async (file: File, values: any) => {
    const boundary = `----Boundary${Math.random().toString().substr(2, 10)}`;
    const fileBuffer = await file.arrayBuffer();
    const fileName = file.name;
    const institutionName =
      sessionStorage.getItem('hospitalCode') ||
      localStorage.getItem('hospitalCode')
    const patientDemographic = {
      patientId: patientData?.uhid,
      accessionNumber: patientData?.patientId,
      patientName: patientData?.name,
      institutionName,
      gender: patientData?.gender
    }
    const modifiedBuffer = modifyDICOMTags(fileBuffer, patientDemographic);

    // Create multipart payload
    const parts = [
      new Blob([
        `--${boundary}\r\n`,
        `Content-Type: application/dicom\r\n\r\n`,
        modifiedBuffer,
        '\r\n',
        `--${boundary}--\r\n`
      ])
    ];

    const payload = new Blob(parts, { type: `multipart/related; boundary=${boundary}` });

    return createUploadRequest(file, payload, boundary, false);
  };

  // Helper function to get content type
  const getContentType = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'png': return 'image/png';
      case 'tiff': return 'image/tiff';
      case 'bmp': return 'image/bmp';
      default: return 'application/octet-stream';
    }
  };
  const generateDicomUid = (rootOid = "2.25") => {
    const timestamp = Date.now(); // Current timestamp
    const randomNum = Math.floor(Math.random() * 1e9); // Random number
    return `${rootOid}.${timestamp}${randomNum}`; // Combine root OID with timestamp and random number
  };


  const modifyDICOMTags = (
    fileBuffer: ArrayBuffer,
    patientDemographics: {
      patientName: string;
      patientId: string;
      accessionNumber: string;
      institutionName: string;
      gender: string;
    }
  ): ArrayBuffer => {
    const dicomData = dcmjs.data.DicomMessage.readFile(fileBuffer);
    const dataset = dcmjs.data.DicomMetaDictionary.naturalizeDataset(dicomData.dict);

    // Set IssuerOfPatientID to match existing patient
    dataset.IssuerOfPatientID = patientDemographics.institutionName;

    // Modify Patient Name (0010,0010)
    if (dataset.PatientName && Array.isArray(dataset.PatientName)) {
      let currentName = dataset.PatientName[0]?.Alphabetic;

      if (currentName !== patientDemographics.patientName) {
        dataset.PatientName[0].Alphabetic = patientDemographics.patientName;
      }
    } else if (!dataset.PatientName || !Array.isArray(dataset.PatientName)) {
      // If PatientName is not present or not in the expected format,
      dataset.PatientName[0].Alphabetic = patientDemographics.patientName;
    }

    // Modify Patient ID (0010,0020)
    if (dataset.PatientID !== patientDemographics.patientId) {
      dataset.PatientID = patientDemographics.patientId;
    }

    // Modify Accession Number (0008,0050)
    if (dataset.AccessionNumber !== patientDemographics.accessionNumber) {
      dataset.AccessionNumber = patientDemographics.accessionNumber;
    }

    // Modify Institution Name (0008,0080)
    if (dataset.InstitutionName !== patientDemographics.institutionName) {
      dataset.InstitutionName = patientDemographics.institutionName;
    }

    // Modify Patient's Sex (0010,0040)
    if (dataset.PatientSex !== patientDemographics.gender) {
      dataset.PatientSex = patientDemographics.gender;
    }

    // Denaturalize the dataset and write the updated DICOM file
    dicomData.dict = dcmjs.data.DicomMetaDictionary.denaturalizeDataset(dataset);
    const updatedBuffer = dicomData.write();
    return updatedBuffer;
  };
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void,
    isDicomSelected: boolean
  ) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;
   resetState();
    // Reset error states when new files are selected
  setIsError(false);
  setErrorMessage('');
  setIsImportDisabled(false);

    const filesArray = Array.from(selectedFiles);
    const selectedFilesList: File[] = [];
    setIsUnzipping(true);
    setIsError(false);
    setErrorMessage('');

    try {
      // Handle ZIP file
      if (filesArray.length === 1 && filesArray[0].name.endsWith('.zip')) {
        const zipFile = filesArray[0];
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(zipFile);

        for (const [fileName, fileData] of Object.entries(zipContent.files)) {
          if (fileData.dir) continue;
          const blob = await fileData.async('blob');
          const file = new File([blob], fileName, { type: getContentType(fileName) });

          if (isDicomSelected) {
            const patientModality = [patientData?.modality, ...(patientData?.appointmentDetails?.extraModalities?.map((item: any) => item.modality) || [])];
            const { isValid, error } = await isDicomFile(file, patientModality);
            if (isValid) {
              selectedFilesList.push(file);
            } else {
              setIsError(true);
              setErrorMessage(error || `Invalid DICOM file: ${fileName}.`);
              setIsImportDisabled(true);
              return;
            }
          } else {
            const isNonDicom = await isNonDicomFile(file, fileName);
            if (isNonDicom) {
              selectedFilesList.push(file);
            } else {
              setIsError(true);
              setErrorMessage(`Invalid file: ${fileName}. Please upload a valid non-DICOM file.`);
              setIsImportDisabled(true);
              return;
            }
          }
        }
      } else {
        // Handle individual files or folder
        for (const file of filesArray) {
          const fileName = file.webkitRelativePath || file.name;
          if (isDicomSelected) {
            const patientModality = [patientData?.modality, ...(patientData?.appointmentDetails?.extraModalities?.map((Facet, item: any) => item.modality) || [])];
            const { isValid, error } = await isDicomFile(file, patientModality);
            if (isValid) {
              selectedFilesList.push(file);
            } else {
              setIsError(true);
              setErrorMessage(error || `Invalid DICOM file: ${fileName}.`);
              setIsImportDisabled(true);
              return;
            }
          } else {
            const isNonDicom = await isNonDicomFile(file, fileName);
            if (isNonDicom) {
              selectedFilesList.push(file);
            } else {
              setIsError(true);
              setErrorMessage(`Invalid file: ${fileName}. Please upload a valid non-DICOM file.`);
              setIsImportDisabled(true);
              return;
            }
          }
        }
      }

      setIsError(false);
      setErrorMessage('');
      setIsImportDisabled(false);
      setFiles(selectedFilesList);
      setFieldValue('fileSelection', selectedFilesList);
      setFileName(selectedFilesList.length > 0 ? `${selectedFilesList.length} file(s) selected` : 'No files selected');
    } catch (error) {
      console.error('Error processing files:', error);
      setIsError(true);
      setErrorMessage('Failed to process files.');
      setIsImportDisabled(true);
    } finally {
      setIsUnzipping(false);
    }
  };

  const isNonDicomFile = async (file: File, fileName: string): Promise<boolean> => {
    const validExtensions = ['jpg', 'jpeg', 'png', 'tiff', 'bmp'];
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension && validExtensions.includes(extension)) {
      return true;
    }

    // Optionally, you can check the MIME type if needed
    const validMimeTypes = ['image/jpeg', 'image/png', 'image/tiff', 'image/bmp'];
    return validMimeTypes.includes(file.type);
  };

  // Function to check if a file is a valid DICOM file and validate modality
  const isDicomFile = async (
    file: Blob,
    patientModality: string | string[]
  ): Promise<{ isValid: boolean; modality?: string; error?: string }> => {
    try {
      const buffer = await file.arrayBuffer();
      const dicomData = dcmjs.data.DicomMessage.readFile(buffer);
      const dataset = dcmjs.data.DicomMetaDictionary.naturalizeDataset(dicomData.dict);

      // Extract modality from DICOM metadata (tag 00080060)
      const dicomModality = dataset.Modality;

      // If DICOM file has no modality tag, allow the upload
      if (!dicomModality) {
        return { isValid: true };
      }

      // If patientModality is undefined or empty, allow the upload
      if (!patientModality || (Array.isArray(patientModality) && patientModality.length === 0)) {
        return { isValid: true, modality: dicomModality };
      }

      // Convert patientModality to array if it's a string
      const patientModalities = Array.isArray(patientModality) ? patientModality : [patientModality];

      // Perform case-insensitive comparison
      const isModalityMatch = patientModalities.some(
        (modality) => modality.toLowerCase() === dicomModality.toLowerCase()
      );

      if (!isModalityMatch) {
        return {
          isValid: false,
          error: `Modality mismatch: DICOM file modality (${dicomModality}) does not match patient modality (${patientModalities.join(', ')}).`,
        };
      }

      return { isValid: true, modality: dicomModality };
    } catch (error) {
      return { isValid: false, error: 'Invalid DICOM file.' };
    }
  };

  const handleDivClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCloseSuccessPopup = () => {
    setIsSuccessOpen(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };



  const resetState = () => {
    setFiles([]);
    setFileName('');
    setIsError(false);
    setErrorMessage('');
    setIsImportDisabled(false);
    resetUploads();
  };

  useEffect(() => {
    fetchCommonData();
  }, [])

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [selectedOption, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedOption('DICOM');
      setIsUnzipping(false);
      resetState();
    }
  }, [isOpen, patientData]);

  if (!isOpen && !isSuccessOpen) return null;

  return (
    <>
      {/* Study Import Modal */}
      {isOpen && (
        <div className='form__section'>
        <div className="modal-overlay popup-overlay fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }} >
          <div className="modal-container  bg-white p-6 rounded-lg shadow-lg" style={{ width: "50rem", maxHeight: "90vh", overflowY: "auto" }}>
            <div className="flex justify-between border-b pb-4">
              <h2 className="modal-heading popup-heading-text" style={
                isDarkMode ? { color: '#FFFFFF' } : { color: '#000000' }
              }>Import Study</h2>
              <div className="text-xl cursor-pointer" onClick={onClose}>X</div>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={getValidationSchema(selectedOption)}
              onSubmit={handleImport} // Trigger confirmation popup on form submit
            >
              {({
                values,
                errors,
                handleChange,
                handleBlur,
                touched,
                setFieldValue,
              }) => (
                <Form>
                  <div className="mt-4">
                    <div className="mb-4">
                      <div className='saveAsOption'>
                        <div>Select File Type :</div>
                        <div className="form__radio__option mb_0">
                          <input type="radio" name="fileType"
                            value="DICOM"
                            checked={selectedOption === "DICOM"}
                            onChange={() => setSelectedOption("DICOM")}
                            className="form__radio__input" />
                          <label className="form__radio__label draft-btn" htmlFor="fileType">DICOM</label>
                        </div>
                        <div className="form__radio__option mb_0">
                          <input type="radio" name="fileType"
                            value="nonDICOM"
                            checked={selectedOption === "nonDICOM"}
                            onChange={() => setSelectedOption("nonDICOM")}
                            className="form__radio__input" />
                          <label className="form__radio__label complete-btn" htmlFor="fileType">Non DICOM</label>
                        </div>
                      </div>
                      {/* File selection */}
                      <div className='formRow formRow__2' style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div className='formCol' style={{ flex: 1 }}>
                          <div className='form__Field'>
                            <FileInput
                              id="fileSelection"
                              name="fileSelection"
                              label="Select Zip or File(s)"
                              placeholder="Select Zip or File(s)"
                              isFolderInput={false}
                              accept={selectedOption === 'DICOM' ? '.dcm,.zip' : '.jpg,.jpeg,.png,.tiff,.bmp,.zip'}
                              onChange={(e) => handleFileChange(e, setFieldValue, selectedOption === 'DICOM')}
                              onBlur={handleBlur}
                              isDarkMode={isDarkMode}
                            />
                          </div>
                        </div>

                        <div style={{ flex: '0 0 auto' }}>OR</div>

                        <div className='formCol' style={{ flex: 1 }}>
                          <div className="form__Field">
                            <FileInput
                              id="folderSelection"
                              name="folderSelection"
                              label="Select Folder"
                              placeholder="Select Folder"
                              isFolderInput={true}
                              accept={selectedOption === 'DICOM' ? '.dcm' : '.jpg,.jpeg,.png,.tiff,.bmp'}
                              onChange={(e) => handleFileChange(e, setFieldValue, selectedOption === 'DICOM')}
                              onBlur={handleBlur}
                              isDarkMode={isDarkMode}
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        {
                          uploads.map((upload, index) => (
                            <div key={index} className="upload-item">
                              <div className="file-name">{upload.fileName}</div>
                              <div className="progress-container">
                                <progress
                                  value={upload.progress}
                                  max="100"
                                  className={`progress-bar ${upload.error ? 'error' : ''}`}
                                />
                                <span className="progress-text">
                                  {upload.error || `${Math.round(upload.progress)}%`}
                                </span>
                              </div>
                            </div>
                          ))
                        }
                        <ErrorMessage name="fileSelection" component="div" className="error text-error" />
                        {isError ? (
                            <div className="text-sm font-medium text-red-500 mb-4">
                            {errorMessage.includes('cancelled') ? 'Upload cancelled' : errorMessage}
                          </div>
                        ) : fileName ? fileName : null}
                      </div>

                      {selectedOption === "nonDICOM" && (
                        <div>
                          <label className="text-left modal-subheading">Non DICOM Images</label>
                          <div >
                            {/* Patient Id and Patient Name */}
                            <div className='formRow formRow__2'>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='patientId'>
                                    Patient Id
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    id="patientId"
                                    name="patientId"
                                    placeholder="Patient Id"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.patientId}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name="patientId" component="div" className="error text-error" />

                                </div>
                              </div>
                              {/* Patient Name */}
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label">
                                    Patient Name
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    id="patientName"
                                    name="patientName"
                                    placeholder="Patient Name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.patientName}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name="patientName" component="div" className="error text-error" />
                                </div>
                              </div>
                            </div>
                            {/* Age and gender */}
                            <div className='formRow formRow__2'>
                              <div className='formCol'>
                                {/* Age*/}
                                <div className="form__Field">
                                  <label className="form__label" htmlFor="age">
                                    Age
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    className="form__input form__input--sm"
                                    type="text"
                                    id="age"
                                    name="age"
                                    placeholder="Enter Age"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.age}
                                    min="0"
                                  />
                                  <ErrorMessage name="age" component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label" htmlFor="gender">
                                    Sex
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <div className="form__radio">
                                    <label className="flex items-center mr-8 custom-radio-container">
                                      <input
                                        type="radio"
                                        name="gender"
                                        value="Male"
                                        checked={values.gender === "Male"}
                                        onChange={handleChange}
                                        className="form__radio__input"
                                      />
                                      <span className="form__radio__label">Male</span>
                                    </label>
                                    <label className="flex items-center mr-8 custom-radio-container">
                                      <input
                                        type="radio"
                                        name="gender"
                                        value="Female"
                                        checked={values.gender === "Female"}
                                        onChange={handleChange}
                                        className="form__radio__input"
                                      />
                                      <span className="form__radio__label">Female</span>
                                    </label>
                                    <label className="flex items-center mr-8 custom-radio-container">
                                      <input
                                        type="radio"
                                        name="gender"
                                        value="Other"
                                        checked={values.gender === "Other"}
                                        onChange={handleChange}
                                        className="form__radio__input"
                                      />
                                      <span className="form__radio__label">Other</span>
                                    </label>
                                  </div>
                                  <ErrorMessage name="gender" component="div" className="error text-error" />
                                </div>
                              </div>
                            </div>
                            {/* study date and modality */}
                            <div className='formRow formRow__2'>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='studyDate'>
                                    Study Date
                                    <span className="required-asterisk">*</span></label>
                                  <div className="form__date-container">
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                      <Field name="studyDate" style={{ width: '100%' }}>
                                        {({ field }) => (
                                          <DatePicker
                                            sx={{
                                              width: '100%',
                                              '& .MuiInputBase-root': {
                                                backgroundColor: '#F6FFFF',
                                              },
                                              '& .MuiInputBase-input': {
                                                padding: '10px',
                                              },
                                            }}
                                            {...field}
                                            value={field.value || null}
                                            format="DD/MM/YYYY"
                                            onChange={(newValue) => {
                                              setFieldValue(field.name, newValue);
                                            }}
                                            renderInput={(params) => (
                                              <TextField
                                                {...params}
                                                error={touched.studyDate && Boolean(errors.studyDate)}
                                                helperText={touched.studyDate && errors.studyDate}
                                              />
                                            )}
                                            componentsProps={{
                                              popper: {
                                                // Render the calendar in a Portal
                                                container: document.body,
                                              },
                                            }}
                                          />
                                        )}
                                      </Field>
                                    </LocalizationProvider>
                                  </div>
                                  <ErrorMessage name="studyDate" component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='modality'>
                                    Modalities
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <Select
                                    id='modality'
                                    placeholder='Select'
                                    //className='custom-bg-white'
                                    isMulti={true}
                                    isClearable={false}
                                    isSearchable={true}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    options={ModalityOptions}
                                    value={values?.modality}
                                    onChange={(selectedOptions) => {
                                      const newModalities = selectedOptions ? selectedOptions.map(option => option) : [];
                                      setFieldValue('modality', newModalities);
                                    }}
                                  />
                                </div>
                                <ErrorMessage name="modality" component="div" className="error text-error" />

                              </div>
                            </div>
                            {/* Study Description and Accession Number  */}
                            <div className='formRow formRow__2'>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='studyDesc'>Study Description
                                  </label>
                                  <input
                                    type="text"
                                    id="studyDesc"
                                    name="studyDesc"
                                    placeholder="Study Description"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.studyDesc}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name="studyDesc" component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='accessionNum'>Accession Number
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    id="accessionNum"
                                    placeholder="Accession Number"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.accessionNum}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name="accessionNum" component="div" className="error text-error" />
                                </div>
                              </div>
                            </div>
                            {/* Refering Physician and Series Description  */}
                            <div className='formRow formRow__2'>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='refPhysician'>Referring Physician </label>
                                  <input
                                    type="text"
                                    id="refPhysician"
                                    name="refPhysician"
                                    placeholder="Referring Physician"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.refPhysician}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name="refPhysician" component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='seriesDesc'>Series Description </label>
                                  <input
                                    type="text"
                                    id="seriesDesc"
                                    placeholder="Series Description"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.seriesDesc}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name="seriesDesc" component="div" className="error text-error" />
                                </div>
                              </div>
                            </div>
                            <div className='formRow formRow__2'>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='studyRegion'>Study Region </label>
                                  <input
                                    type="text"
                                    id="studyRegion"
                                    name="studyRegion"
                                    placeholder="Study Region"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.studyRegion}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name="studyRegion" component="div" className="error text-error" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                    <div className="modal-actions flex justify-start gap-4">
                      {isUploading ? (
                        <button
                          type="button"
                          className="custom-button cancel-upload-btn"
                          onClick={handleCancelUploads}
                        >
                          Cancel Upload
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="custom-button popup-button"
                          onClick={onClose}
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        className="custom-button main-theme save-btn"
                        disabled={files.length === 0 || isUploading|| isImportDisabled}
                      >
                        Import
                      </button>
                    </div>
                  </div>
                  {isUnzipping && (
                    <div className="unzip-loader">
                      <div className="loader"></div>
                      <span>Processing files...</span>
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          </div>
          {isSuccessOpen && (
            <SuccessModal
              isSuccessModalOpen={isSuccessOpen}
              title={successMessage}
              handleCloseModal={handleCloseSuccessPopup}
            />
          )}
        </div>
        </div>
      )}
    </>
  );
};

export default StudyUploadModal;
